/**
 * GoersManualPool — pool nomor tiket untuk event bertanggal tetap (list manual dari GOERS).
 *
 * Called by: TourismDevelopment.jsx (memberId="itdc"), DestinationManagement.jsx (memberId="twc")
 * Uses: goersApi → POST /inventory/import, GET /inventory?member_code=...
 *
 * GOERS mengirim nomor tiket sebagai list (bukan API). Satu list bisa berisi event milik
 * member berbeda, jadi setiap baris pool disimpan dengan member_code dan panel hanya
 * menampilkan/menulis milik member halaman ini — tiket ITDC tidak akan tercampur ke TWC.
 *
 * Nomor tiket bukan produk: tombol "Jadikan Produk" membuat produknya dengan tag
 * [GOERS:<ticket_id>] pada namanya, dan tag itulah yang dipakai webhook pembayaran
 * untuk mengambil nomor dari pool yang tepat.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, HStack, VStack, Heading, Text, Button, Input, Select, Textarea, useToast,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Card, CardBody, Badge,
  SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Code, Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiUploadCloud, FiDownloadCloud } from 'react-icons/fi';
import { goersApi } from '../config/api';

/**
 * Event bertanggal tetap per member. ticket_id sintetis (list manual tidak punya
 * ticket_id dari GOERS) dan harus sama persis dengan tag [GOERS:<id>] di nama komponen
 * paket. Konvensi blok nomor supaya tidak pernah bentrok antar member:
 *   ITDC → 9009xx · TWC → 9019xx
 */
const PRODUCTS_BY_MEMBER = {
  itdc: {
    label: 'ITDC',
    contentName: 'MotoGP Mandalika',
    products: [
      { ticket_id: 900901, ticket_name: 'MotoGP Mandalika Hari 1', schedule_date: '2026-10-09' },
      { ticket_id: 900902, ticket_name: 'MotoGP Mandalika Hari 2', schedule_date: '2026-10-10' },
      { ticket_id: 900903, ticket_name: 'MotoGP Mandalika Hari 3', schedule_date: '2026-10-11' },
      { ticket_id: 900904, ticket_name: 'MotoGP Mandalika 3-Day Pass', schedule_date: '2026-10-09' },
    ],
  },
  twc: {
    label: 'TWC',
    contentName: '',
    // Belum ada event TWC berkuota yang disepakati — dipakai lewat mode "Event lain".
    products: [],
  },
};

const CUSTOM = '__custom__';

const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const GoersManualPool = ({ memberId = 'itdc' }) => {
  const toast = useToast();
  const member = PRODUCTS_BY_MEMBER[memberId] || { label: memberId.toUpperCase(), contentName: '', products: [] };
  const presets = member.products;

  const [selection, setSelection] = useState(presets.length ? String(presets[0].ticket_id) : CUSTOM);
  const [custom, setCustom] = useState({ ticket_id: '', ticket_name: '', schedule_date: '' });
  const [numbers, setNumbers] = useState('');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchDates, setBatchDates] = useState({});
  const [savingBatch, setSavingBatch] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  // Sync window filters on when GOERS created the batch order, not on ticket validity.
  // Defaults to the last 7 days: batches are usually generated a few days ahead.
  const [syncStart, setSyncStart] = useState(daysAgo(7));
  const [syncEnd, setSyncEnd] = useState(daysAgo(0));

  const isCustom = selection === CUSTOM;
  const selectedProduct = isCustom
    ? { ticket_id: custom.ticket_id, ticket_name: custom.ticket_name, schedule_date: custom.schedule_date }
    : presets.find((p) => String(p.ticket_id) === selection) || presets[0];

  const parsedNumbers = useMemo(
    () => [...new Set(numbers.split('\n').map((n) => n.trim()).filter(Boolean))],
    [numbers]
  );

  const fetchPool = useCallback(async () => {
    setIsLoading(true);
    try {
      // Server filters by member_code, so another member's tickets can never show up here.
      const res = await goersApi.get('/inventory', { params: { limit: 500, member_code: memberId } });
      setRows(res?.data?.data?.rows || []);
      setStats(res?.data?.data?.stats_by_ticket || []);
      setBatches(res?.data?.data?.batches || []);
    } catch (err) {
      toast({ title: 'Gagal memuat pool', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [memberId, toast]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  /**
   * Pull tickets GOERS pre-generated into our inventory via their API. Replaces the
   * sync-goers-pool.sh script — no token juggling, the axios instance carries it.
   */
  const syncFromGoers = useCallback(async () => {
    setIsSyncing(true);
    try {
      const res = await goersApi.post('/inventory/sync', { start_date: syncStart, end_date: syncEnd });
      const s = res?.data?.data || {};
      const failed = (s.errors || []).length;
      toast({
        title: 'Sync selesai',
        description:
          `${s.ordersScanned || 0} order · ${s.ticketsSeen || 0} tiket dibaca · ` +
          `${s.inserted || 0} baru · ${s.updated || 0} diperbarui` +
          (s.unmatched ? ` · ${s.unmatched} tanpa jenis tiket` : '') +
          (failed ? ` · ${failed} gagal` : ''),
        status: failed ? 'warning' : 'success',
        duration: 8000, isClosable: true,
      });
      fetchPool();
    } catch (err) {
      toast({ title: 'Sync gagal', description: err?.response?.data?.message || err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setIsSyncing(false);
    }
  }, [syncStart, syncEnd, toast, fetchPool]);

  const importPool = useCallback(async () => {
    if (!parsedNumbers.length) {
      toast({ title: 'Tempel nomor tiketnya dulu', description: 'Satu nomor per baris.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!String(selectedProduct?.ticket_id || '').trim() || !String(selectedProduct?.schedule_date || '').trim()) {
      toast({ title: 'Lengkapi jenis tiket', description: 'Ticket ID dan tanggal event wajib diisi.', status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    setIsImporting(true);
    try {
      const res = await goersApi.post('/inventory/import', {
        member_code: memberId,
        ticket_id: selectedProduct.ticket_id,
        ticket_name: selectedProduct.ticket_name,
        content_name: member.contentName || selectedProduct.ticket_name,
        schedule_date: selectedProduct.schedule_date,
        ticket_numbers: parsedNumbers,
      });
      const s = res?.data?.data || {};
      toast({
        title: 'Import selesai',
        description: `${s.inserted || 0} baru · ${s.duplicate || 0} sudah ada di pool · ${s.total || 0} dibaca`,
        status: 'success', duration: 6000, isClosable: true,
      });
      setNumbers('');
      fetchPool();
    } catch (err) {
      toast({ title: 'Import gagal', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setIsImporting(false);
    }
  }, [parsedNumbers, selectedProduct, memberId, member.contentName, toast, fetchPool]);

  /**
   * Satu batch = (source_order_number, ticket_id), sama seperti pengelompokan di backend.
   * Nomor order saja tidak unik: semua import manual memakai 'MANUAL', jadi tanpa ticket_id
   * keempat jenis tiket MotoGP akan berbagi satu input tanggal.
   */
  const batchKey = (b) => `${b.source_order_number}::${b.ticket_id}`;

  /**
   * Record how long a delivered batch stays usable. GOERS states this per supplied
   * order ("10 pertama hari ini, 10 sisanya 30 hari") and their API does not carry it.
   */
  const saveBatchValidity = useCallback(async (batch) => {
    const key = batchKey(batch);
    const value = batchDates[key] !== undefined
      ? batchDates[key]
      : (batch.valid_until ? String(batch.valid_until).slice(0, 10) : '');
    setSavingBatch(key);
    try {
      const res = await goersApi.post('/inventory/batch-validity', {
        source_order_number: batch.source_order_number,
        ticket_id: batch.ticket_id,
        valid_until: value || '',
      });
      toast({
        title: value ? 'Masa berlaku disimpan' : 'Masa berlaku dikosongkan',
        description: `${res?.data?.data?.affected || 0} tiket pada batch ${batch.ticket_name || batch.source_order_number}`,
        status: 'success', duration: 5000, isClosable: true,
      });
      fetchPool();
    } catch (err) {
      toast({ title: 'Gagal menyimpan', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSavingBatch(null);
    }
  }, [batchDates, toast, fetchPool]);

  /**
   * Buat produk PRIME untuk satu jenis tiket di pool — jalur yang sama dengan import
   * GOERS Content (POST /import), jadi nama produk otomatis membawa tag [GOERS:<id>]
   * dan MEMBER_CODE member halaman ini. Setiap penjualan produk itu mengambil satu nomor.
   */
  const createProduct = useCallback(async (card) => {
    // ponytail: native prompt for the one number the list doesn't carry; a form if more fields show up.
    const price = window.prompt(`Harga jual ${card.ticket_name} (IDR)`, '0');
    if (price === null) return;
    if (!/^\d+$/.test(price.trim())) {
      toast({ title: 'Harga harus angka', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setCreatingProduct(card.ticket_id);
    try {
      const res = await goersApi.post('/import', {
        memberCode: memberId,
        ticketId: card.ticket_id,
        ticketName: card.ticket_name,
        contentName: member.contentName && !card.ticket_name.startsWith(member.contentName) ? member.contentName : undefined,
        price: Number(price.trim()),
      });
      toast({ title: 'Produk dibuat', description: res?.data?.message, status: 'success', duration: 5000, isClosable: true });
    } catch (err) {
      toast({ title: 'Gagal membuat produk', description: err?.response?.data?.message || err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setCreatingProduct(null);
    }
  }, [memberId, member.contentName, toast]);

  // stats_by_ticket = satu baris per (ticket_id, status) → dilipat jadi total per produk.
  const stockByProduct = useMemo(() => {
    const map = {};
    for (const s of stats) {
      const key = String(s.ticket_id);
      if (!map[key]) map[key] = { ticket_name: s.ticket_name, schedule_date: s.schedule_date, available: 0, sold: 0, expired: 0 };
      const n = Number(s.count) || 0;
      // EXPIRED datang dari backend untuk baris yang lewat valid_until: masih di pool,
      // tapi ditolak saat alokasi. Tanpa bucket sendiri dia terhitung "terpakai",
      // padahal tidak ada yang membelinya.
      if (s.status === 'IN_POOL') map[key].available += n;
      else if (s.status === 'EXPIRED') map[key].expired += n;
      else map[key].sold += n;
    }
    return map;
  }, [stats]);

  // Kartu stok: preset dulu, lalu ticket_id lain yang sudah ada isinya di pool member ini.
  const stockCards = useMemo(() => {
    const cards = presets.map((p) => ({
      ticket_id: p.ticket_id,
      ticket_name: p.ticket_name,
      schedule_date: p.schedule_date,
      ...(stockByProduct[String(p.ticket_id)] || { available: 0, sold: 0, expired: 0 }),
    }));
    const presetIds = new Set(presets.map((p) => String(p.ticket_id)));
    for (const [id, s] of Object.entries(stockByProduct)) {
      if (!presetIds.has(id)) {
        cards.push({
          ticket_id: id,
          ticket_name: s.ticket_name || `Ticket ${id}`,
          schedule_date: s.schedule_date ? String(s.schedule_date).slice(0, 10) : '—',
          available: s.available, sold: s.sold, expired: s.expired,
        });
      }
    }
    return cards;
  }, [presets, stockByProduct]);

  return (
    <Box>
      <HStack mb={2} justify="space-between" wrap="wrap" gap={2}>
        <HStack gap={2}>
          <Heading size="md">Pool Tiket Event</Heading>
          <Badge colorScheme="blue">{member.label}</Badge>
        </HStack>
        <Button leftIcon={<FiRefreshCw />} size="sm" variant="outline" onClick={fetchPool} isLoading={isLoading}>
          Refresh
        </Button>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Tempel list nomor tiket yang dikirim GOERS untuk event {member.label}. Tanggal berlaku
        mengikuti jenis produk — agen tidak memilih tanggal. Nomor yang sama tidak akan masuk
        dua kali, satu nomor hanya bisa terpakai oleh satu order, dan pool member lain tidak
        pernah tampil di panel ini.
      </Text>

      {stockCards.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3} mb={4}>
          {stockCards.map((c) => (
            <Card key={c.ticket_id} bg="white" borderRadius="12px" boxShadow="sm">
              <CardBody py={3}>
                <Stat>
                  <StatLabel fontSize="xs" noOfLines={1}>{c.ticket_name}</StatLabel>
                  <StatNumber fontSize="2xl" color={c.available === 0 ? 'red.500' : 'green.600'}>{c.available}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    tersedia · {c.sold} terpakai
                    {c.expired > 0 && (
                      <Text as="span" color="red.500"> · {c.expired} hangus</Text>
                    )}
                    {' · '}{c.schedule_date}
                  </StatHelpText>
                </Stat>
                <Button size="xs" mt={2} variant="outline" colorScheme="blue"
                  isLoading={creatingProduct === c.ticket_id} onClick={() => createProduct(c)}>
                  Jadikan Produk
                </Button>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Tickets GOERS generated through their API — pulled, not pasted. */}
      <Card bg="white" borderRadius="16px" boxShadow="sm" mb={4}>
        <CardBody>
          <HStack justify="space-between" wrap="wrap" gap={3}>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="600">Tarik tiket dari API GOERS</Text>
              <Text fontSize="xs" color="gray.500">
                Untuk tiket yang di-generate GOERS ke inventory kita. Rentang tanggal = kapan
                batch order dibuat GOERS, bukan masa berlaku tiket.
              </Text>
            </VStack>
            <HStack gap={2} wrap="wrap">
              <Input type="date" size="sm" w="auto" value={syncStart} onChange={(e) => setSyncStart(e.target.value)} />
              <Text fontSize="sm" color="gray.500">s/d</Text>
              <Input type="date" size="sm" w="auto" value={syncEnd} onChange={(e) => setSyncEnd(e.target.value)} />
              <Button leftIcon={<FiDownloadCloud />} colorScheme="teal" size="sm"
                onClick={syncFromGoers} isLoading={isSyncing} loadingText="Menarik...">
                Tarik dari GOERS
              </Button>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      {batches.length > 0 && (
        <Card bg="white" borderRadius="16px" boxShadow="sm" mb={4}>
          <CardBody>
            <Text fontSize="sm" fontWeight="600" mb={1}>Batch tiket & masa berlaku</Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              GOERS menetapkan masa berlaku per batch yang dikirim, dan itu tidak ikut di API —
              jadi isi di sini. Tiket yang paling cepat kedaluwarsa akan dijual lebih dulu, dan
              yang sudah lewat tanggalnya tidak akan diberikan ke pembeli.
            </Text>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr><Th>Batch (order GOERS)</Th><Th>Tiket</Th><Th isNumeric>Sisa / Total</Th><Th>Berlaku sampai</Th><Th></Th></Tr>
                </Thead>
                <Tbody>
                  {batches.map((b) => {
                    const key = batchKey(b);
                    const src = b.source_order_number;
                    const current = b.valid_until ? String(b.valid_until).slice(0, 10) : '';
                    const value = batchDates[key] !== undefined ? batchDates[key] : current;
                    const expired = current && current < daysAgo(0);
                    return (
                      <Tr key={key}>
                        <Td fontFamily="mono" fontSize="xs">
                          {src === 'MANUAL' ? <Badge colorScheme="purple">List manual</Badge> : src}
                        </Td>
                        <Td fontSize="sm">{b.ticket_name || `#${b.ticket_id}`}</Td>
                        <Td isNumeric fontSize="sm">
                          <Text as="span" color={Number(b.available) === 0 ? 'red.500' : 'green.600'} fontWeight="600">
                            {b.available}
                          </Text>
                          {' / '}{b.total}
                        </Td>
                        <Td>
                          <HStack>
                            <Input type="date" size="xs" w="auto" value={value}
                              onChange={(e) => setBatchDates((p) => ({ ...p, [key]: e.target.value }))} />
                            {expired && <Badge colorScheme="red" fontSize="xs">kedaluwarsa</Badge>}
                            {!current && <Badge colorScheme="gray" fontSize="xs">belum diisi</Badge>}
                          </HStack>
                        </Td>
                        <Td>
                          <Button size="xs" colorScheme="green" isLoading={savingBatch === key} loadingText="..."
                            onClick={() => saveBatchValidity(b)}>
                            Simpan
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      <Card bg="white" borderRadius="16px" boxShadow="sm" mb={4}>
        <CardBody>
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" fontWeight="600">Import list nomor tiket (event bertanggal tetap)</Text>
            <HStack wrap="wrap" gap={2}>
              <Select size="sm" w="auto" value={selection} onChange={(e) => setSelection(e.target.value)}>
                {presets.map((p) => (
                  <option key={p.ticket_id} value={p.ticket_id}>
                    {p.ticket_name} — {p.schedule_date}
                  </option>
                ))}
                <option value={CUSTOM}>Event lain (isi manual)…</option>
              </Select>
              {!isCustom && selectedProduct && (
                <>
                  <Badge colorScheme="purple" fontSize="xs">[GOERS:{selectedProduct.ticket_id}]</Badge>
                  <Text fontSize="xs" color="gray.500">
                    salin tag ini ke belakang nama produk di Bundling Product
                  </Text>
                </>
              )}
            </HStack>

            {isCustom && (
              <HStack wrap="wrap" gap={2}>
                <Input size="sm" w="auto" placeholder="Ticket ID (angka)" value={custom.ticket_id}
                  onChange={(e) => setCustom((c) => ({ ...c, ticket_id: e.target.value.replace(/\D/g, '') }))} />
                <Input size="sm" w="240px" placeholder="Nama tiket" value={custom.ticket_name}
                  onChange={(e) => setCustom((c) => ({ ...c, ticket_name: e.target.value }))} />
                <Input size="sm" w="auto" type="date" value={custom.schedule_date}
                  onChange={(e) => setCustom((c) => ({ ...c, schedule_date: e.target.value }))} />
                {custom.ticket_id && <Badge colorScheme="purple" fontSize="xs">[GOERS:{custom.ticket_id}]</Badge>}
              </HStack>
            )}

            <Textarea
              size="sm" rows={5} fontFamily="mono" placeholder={'Satu nomor tiket per baris\nMGP-D1-001\nMGP-D1-002'}
              value={numbers} onChange={(e) => setNumbers(e.target.value)}
            />
            <HStack justify="space-between" wrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.600">
                {parsedNumbers.length} nomor unik siap diimport ke{' '}
                <b>{selectedProduct?.ticket_name || 'jenis tiket yang dipilih'}</b> ({member.label})
              </Text>
              <Button leftIcon={<FiUploadCloud />} colorScheme="blue" size="sm"
                onClick={importPool} isLoading={isImporting} loadingText="Importing..."
                isDisabled={!parsedNumbers.length}>
                Import ke Pool
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {rows.length === 0 ? (
        <Card bg="white" borderRadius="16px" boxShadow="sm" mb={6}>
          <CardBody><VStack py={6} spacing={1}>
            <Text color="gray.400">Pool {member.label} masih kosong</Text>
            <Text color="gray.400" fontSize="sm" textAlign="center">
              Import nomor tiket di atas, lalu buat produknya dengan tag{' '}
              <Code fontSize="xs">[GOERS:{presets[0]?.ticket_id || '9009xx'}]</Code> di Bundling Product.
            </Text>
          </VStack></CardBody>
        </Card>
      ) : (
        <TableContainer mb={6} maxH="360px" overflowY="auto">
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr><Th>Ticket Number</Th><Th>Produk</Th><Th>Asal</Th><Th>Tanggal</Th><Th>Status</Th><Th>Order</Th></Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id}>
                  <Td fontFamily="mono" fontSize="xs">{r.ticket_number}</Td>
                  <Td fontSize="sm">{r.ticket_name || <Text as="span" color="gray.400">—</Text>}</Td>
                  <Td>
                    <Badge fontSize="xs" colorScheme={r.source_order_number === 'MANUAL' ? 'purple' : 'teal'}>
                      {r.source_order_number === 'MANUAL' ? 'List manual' : 'API GOERS'}
                    </Badge>
                  </Td>
                  <Td fontSize="sm">{r.schedule_date ? String(r.schedule_date).slice(0, 10) : <Text as="span" color="gray.400">not set</Text>}</Td>
                  <Td>
                    <Badge fontSize="xs" colorScheme={Number(r.is_expired) ? 'red' : r.status === 'IN_POOL' ? 'green' : r.status === 'DELIVERED' ? 'blue' : 'orange'}>
                      {Number(r.is_expired) ? 'EXPIRED' : r.status}
                    </Badge>
                  </Td>
                  <Td fontSize="xs" fontFamily="mono">{r.prime_order_ref || <Text as="span" color="gray.400">—</Text>}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Divider mb={6} />
    </Box>
  );
};

export default GoersManualPool;
